// Delhi NCR prefixes, the same ones used to measure delivery days (trust_facts_2026-10-31.md).
const NCR_PREFIXES = ['110', '1220', '1210', '2013', '2010'];
// First two digits that no Indian PIN code uses (Army Post Office codes start with 9).
const UNUSED_FIRST_TWO = [29, 35, 54, 55, 65, 66];
// The buyer's PIN code, remembered in this browser only, so every page shows it.
const STORAGE_KEY = 'alekhyam-pin';

/** @param {string} pin */
function isIndianPin(pin) {
  if (!/^[1-8][0-9]{5}$/.test(pin)) return false;
  const firstTwo = Number(pin.slice(0, 2));
  return firstTwo >= 11 && !UNUSED_FIRST_TWO.includes(firstTwo);
}

/**
 * Usual working days for the area a PIN code belongs to: Delhi NCR, Maharashtra
 * (40-44, but 403 is Goa), Karnataka (56-59), or the rest of India.
 * @param {string} pin
 * @param {{ ncr: string, maharashtra: string, karnataka: string, rest: string }} days
 */
function usualDays(pin, days) {
  if (NCR_PREFIXES.some((prefix) => pin.startsWith(prefix))) return days.ncr;
  const firstTwo = Number(pin.slice(0, 2));
  if (firstTwo >= 40 && firstTwo <= 44 && !pin.startsWith('403')) return days.maharashtra;
  if (firstTwo >= 56 && firstTwo <= 59) return days.karnataka;
  return days.rest;
}

function readStoredPin() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

/** @param {string} pin */
function storePin(pin) {
  try {
    window.localStorage.setItem(STORAGE_KEY, pin);
  } catch {
    // Private windows can refuse storage; the check still works for this page.
  }
}

/**
 * "Check your PIN code" on the sketchbook pages: shows the usual delivery time for
 * the buyer's area from our tracked deliveries. It needs no courier key or server.
 * A checked PIN code is remembered and shown as one line with a "Change" button.
 */
class AlekhyamPinCheck extends HTMLElement {
  connectedCallback() {
    this.querySelector('.alekhyam-pin__button')?.addEventListener('click', this.#check);
    this.querySelector('input')?.addEventListener('keydown', this.#onKey);
    this.querySelector('[data-pin-change]')?.addEventListener('click', this.#change);
    const stored = readStoredPin();
    const input = this.querySelector('input');
    if (input && isIndianPin(stored)) {
      input.value = stored;
      this.#check();
    }
  }

  disconnectedCallback() {
    this.querySelector('.alekhyam-pin__button')?.removeEventListener('click', this.#check);
    this.querySelector('input')?.removeEventListener('keydown', this.#onKey);
    this.querySelector('[data-pin-change]')?.removeEventListener('click', this.#change);
  }

  #onKey = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.#check();
  };

  #change = () => {
    const entry = this.querySelector('[data-pin-entry]');
    const summary = this.querySelector('[data-pin-summary]');
    if (entry instanceof HTMLElement) entry.hidden = false;
    if (summary instanceof HTMLElement) summary.hidden = true;
    this.querySelector('input')?.focus();
  };

  #check = () => {
    const input = this.querySelector('input');
    const result = this.querySelector('[data-pin-result]');
    if (!input || !(result instanceof HTMLElement)) return;
    const pin = input.value.replace(/\s+/g, '');
    if (!isIndianPin(pin)) {
      result.hidden = false;
      result.textContent = this.dataset.error || '';
      return;
    }
    const days = usualDays(pin, {
      ncr: this.dataset.daysNcr || '',
      maharashtra: this.dataset.daysMaharashtra || '',
      karnataka: this.dataset.daysKarnataka || '',
      rest: this.dataset.daysRest || '',
    });
    const text = (this.dataset.result || '').replace('[days]', days);
    storePin(pin);
    const summary = this.querySelector('[data-pin-summary]');
    const entry = this.querySelector('[data-pin-entry]');
    if (summary instanceof HTMLElement && entry instanceof HTMLElement) {
      const code = summary.querySelector('[data-pin-code]');
      const line = summary.querySelector('[data-pin-summary-text]');
      if (code) code.textContent = pin;
      if (line) line.textContent = text;
      summary.hidden = false;
      entry.hidden = true;
      result.hidden = true;
      return;
    }
    result.hidden = false;
    result.textContent = text;
  };
}

if (!customElements.get('alekhyam-pin-check')) {
  customElements.define('alekhyam-pin-check', AlekhyamPinCheck);
}
